import {
  Braces,
  Check,
  ChevronsUpDown,
  Database,
  FolderKanban,
  Globe2,
  Layers,
  Loader2,
  Monitor,
  MonitorSmartphone,
  Plus,
  Search,
  Smartphone,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { FaAndroid, FaApple } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import { WorkspaceType, WorkspaceTypeLabels } from "@/common/enums/workspace-type.enum";
import { useAuth } from "@/common/hooks/useAuth";
import { useSetActiveScope } from "@/common/hooks/useSetActiveScope";
import { useToast } from "@/common/hooks/useToast";
import { useProjectList } from "@/common/services/dashboard/dashboard.queries";
import { useProjectSummary } from "@/common/services/settings/project";
import { useFetchWorkspaceDetails } from "@/common/services/users";
import { useGetUserOrgs } from "@/common/services/users/users.queries";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const tooltipDark =
  "z-[1200] border border-rail-border bg-rail text-[12px] font-medium text-rail-foreground shadow-md";

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function workspaceTypeInfo(value: unknown): { label: string; icon: React.ReactNode } {
  const normalized = String(value ?? "")
    .replace(/[\s_-]/g, "")
    .toLowerCase();
  const type =
    normalized === "mobileweb"
      ? WorkspaceType.MobileWeb
      : normalized === "android" || normalized === "androidnative"
        ? WorkspaceType.AndroidNative
        : normalized === "ios" || normalized === "iosnative"
          ? WorkspaceType.IOSNative
          : normalized === "webapplication" || normalized === "web"
            ? WorkspaceType.WebApplication
            : normalized === "rest"
              ? WorkspaceType.Rest
              : normalized === "desktop"
                ? WorkspaceType.Desktop
                : normalized === "database"
                  ? WorkspaceType.Database
                  : normalized === "other"
                    ? WorkspaceType.Other
                    : null;

  if (!type) return { label: String(value || "Workspace"), icon: <FolderKanban className="h-3.5 w-3.5" /> };

  const iconByType: Record<WorkspaceType, React.ReactNode> = {
    [WorkspaceType.WebApplication]: <Globe2 className="h-3.5 w-3.5" />,
    [WorkspaceType.MobileWeb]: <MonitorSmartphone className="h-3.5 w-3.5" />,
    [WorkspaceType.AndroidNative]: <FaAndroid className="h-3.5 w-3.5" />,
    [WorkspaceType.IOSNative]: <FaApple className="h-3.5 w-3.5" />,
    [WorkspaceType.Rest]: <Braces className="h-3.5 w-3.5" />,
    [WorkspaceType.Desktop]: <Monitor className="h-3.5 w-3.5" />,
    [WorkspaceType.Database]: <Database className="h-3.5 w-3.5" />,
    [WorkspaceType.Other]: <FolderKanban className="h-3.5 w-3.5" />,
  };

  return { label: WorkspaceTypeLabels[type], icon: iconByType[type] };
}

/* ─────────────── workspace pane (dark) ─────────────── */

const RailWorkspacePanel: React.FC<{
  projectId: number;
  projectName: string;
  activeWorkspaceId: number | null;
  isApplying: boolean;
  onSelect: (ws: any) => void;
  onAddWorkspace: () => void;
}> = ({ projectId, projectName, activeWorkspaceId, isApplying, onSelect, onAddWorkspace }) => {
  const { data, isLoading } = useProjectSummary(projectId);
  const workspaces: any[] = data?.data?.workspaces ?? [];

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-rail-border px-3 pb-1.5 pt-2">
        <p className="text-[9.5px] font-semibold uppercase tracking-[0.09em] text-rail-muted">
          Workspace in
        </p>
        <p className="mt-0.5 truncate text-[12.5px] font-semibold text-rail-foreground">
          {projectName}
        </p>
      </div>
      <div className="flex-1 overflow-y-auto py-1 no-scrollbar">
        {isLoading ? (
          <div className="flex flex-col gap-1 px-3 py-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-7 w-full animate-pulse rounded-md bg-rail-foreground/[0.06]"
              />
            ))}
          </div>
        ) : workspaces.length === 0 ? (
          <p className="px-3 py-4 text-[12px] text-rail-muted">No workspaces yet.</p>
        ) : (
          workspaces.map((ws: any) => {
            const isActive = Number(ws.id) === activeWorkspaceId;
            const typeInfo = workspaceTypeInfo(ws.workspaceType ?? ws.type ?? ws.platform);
            return (
              <button
                key={ws.id}
                onClick={() => onSelect(ws)}
                disabled={isApplying}
                className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left transition-colors hover:bg-rail-foreground/[0.08] disabled:opacity-50"
              >
                <span className="grid h-4 w-4 shrink-0 place-items-center text-rail-muted" aria-hidden="true">
                  {typeInfo.icon}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[12.5px] text-rail-foreground">
                    {ws.name}
                  </span>
                  <span className="block truncate text-[10px] text-rail-muted">
                    {typeInfo.label}
                  </span>
                </span>
                {isActive && <Check className="h-3.5 w-3.5 shrink-0 text-rail-foreground" />}
              </button>
            );
          })
        )}
      </div>
      <div className="border-t border-rail-border p-1">
        <button
          onClick={onAddWorkspace}
          className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-[12px] text-rail-muted transition-colors hover:bg-rail-foreground/[0.08] hover:text-rail-foreground"
        >
          <Plus className="h-3.5 w-3.5" />
          New workspace
        </button>
      </div>
    </div>
  );
};

/* ─────────────── scope switcher (rail / dark) ─────────────── */

export function ScopeSwitcherRail({
  expanded,
  onOpenChange,
}: {
  expanded: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { userPreference, userPreferenceData, useWorkspaceVersions } = useAuth() as any;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);

  // ── current scope (for the trigger label) ──
  const currentVersionId = userPreferenceData?.data?.versionId;
  const { data: wsDetails } = useFetchWorkspaceDetails(currentVersionId, {
    enabled: !!currentVersionId,
  });
  const ws = wsDetails?.data ?? useWorkspaceVersions?.data;
  const project = ws?.workspace?.project?.name || "Select scope";
  const workspace = ws?.workspace?.name || "—";
  const normalizedWorkspaceType = String(
    ws?.workspace?.workspaceType ?? ws?.workspace?.type ?? ws?.workspaceType ?? ws?.type ?? "",
  )
    .replace(/[\s_-]/g, "")
    .toLowerCase();
  const workspaceType =
    normalizedWorkspaceType === "mobileweb"
      ? WorkspaceType.MobileWeb
      : normalizedWorkspaceType === "android" || normalizedWorkspaceType === "androidnative"
        ? WorkspaceType.AndroidNative
        : normalizedWorkspaceType === "ios" || normalizedWorkspaceType === "iosnative"
          ? WorkspaceType.IOSNative
          : normalizedWorkspaceType === "rest"
            ? WorkspaceType.Rest
            : normalizedWorkspaceType === "desktop"
              ? WorkspaceType.Desktop
              : normalizedWorkspaceType === "database"
                ? WorkspaceType.Database
                : normalizedWorkspaceType === "other"
                  ? WorkspaceType.Other
                  : WorkspaceType.WebApplication;
  const workspaceTypeLabel = WorkspaceTypeLabels[workspaceType];
  const WorkspaceTypeIcon =
    workspaceType === WorkspaceType.MobileWeb
      ? MonitorSmartphone
      : workspaceType === WorkspaceType.AndroidNative || workspaceType === WorkspaceType.IOSNative
        ? Smartphone
        : workspaceType === WorkspaceType.Rest
          ? Braces
          : workspaceType === WorkspaceType.Desktop
            ? Monitor
            : workspaceType === WorkspaceType.Database
              ? Database
              : Globe2;

  const activeProjectId = userPreference?.projectId ? Number(userPreference.projectId) : null;
  const activeWorkspaceId = userPreference?.workspaceId ? Number(userPreference.workspaceId) : null;

  // ── organization (display only) ──
  const { data: orgsData } = useGetUserOrgs();
  const orgs: Array<{ id: number; name: string }> = Array.isArray(orgsData?.data)
    ? orgsData.data
    : [];
  const activeOrgId: number | null = userPreferenceData?.data?.activeOrganizationId ?? null;
  const activeOrg = orgs.find((o) => o.id === activeOrgId) ?? orgs[0] ?? null;
  const orgName = activeOrg?.name ?? "";

  // ── project list (lazy — only when the popover is open) ──
  const { data: projectList, isLoading: isProjectsLoading } = useProjectList(open);
  const projects: any[] = useMemo(() => projectList?.data?.content ?? [], [projectList]);

  const filteredProjects = useMemo(() => {
    if (!search.trim()) return projects;
    const q = search.toLowerCase();
    return projects.filter(
      (p: any) => p.name?.toLowerCase().includes(q) || p.identifier?.toLowerCase().includes(q),
    );
  }, [projects, search]);

  const displayProjectId = selectedProjectId ?? activeProjectId;
  const displayProject = projects.find((p: any) => Number(p.id) === displayProjectId);

  const { setActiveScope, isApplying } = useSetActiveScope({
    soft: true,
    onSuccess: () => {
      // Intentionally left empty: we stay on the current page to preserve context
    },
    onError: (err: any) => {
      showToast({
        title: err?.response?.data?.message || "Failed to switch scope",
        variant: "error",
        autoDismiss: true,
      });
    },
  });

  const changeOpen = (v: boolean) => {
    console.log("changeOpen:", v);
    setOpen(v);
    onOpenChange?.(v);
    if (!v) {
      setSearch("");
      setSelectedProjectId(null);
    }
  };

  const handleSelectWorkspace = (wsItem: any) => {
    if (!wsItem.latestVersion?.id) {
      showToast({
        title: "This workspace has no versions yet — create one first.",
        variant: "error",
        autoDismiss: true,
      });
      return;
    }
    const fromProject = project !== "Select scope" ? project : null;
    const toProject = displayProject?.name ?? "";
    const toastTitle =
      fromProject && fromProject !== toProject
        ? `Switching project from '${fromProject}' to '${toProject}'…`
        : `Switching to ${toProject} / ${wsItem.name}…`;
    changeOpen(false);
    showToast({
      title: toastTitle,
      variant: "info",
      autoDismiss: true,
    });
    setActiveScope({
      projectId: displayProjectId!,
      workspaceId: wsItem.id,
      versionId: wsItem.latestVersion.id,
    });
  };

  const projectInit =
    project && project !== "Select scope" ? project.charAt(0).toUpperCase() : null;

  const trigger = (
    <button
      data-tour="project-switcher"
      aria-label={`Switch project. Current: ${project} / ${workspace} (${workspaceTypeLabel})`}
      className={cn(
        "flex h-11 w-full items-center rounded-md border border-rail-border bg-rail-foreground/[0.03] outline-none transition-all hover:bg-rail-foreground/10 hover:border-rail-border/80",
        open && "bg-rail-foreground/10 border-rail-border",
      )}
      title={`${orgName ? `${orgName} · ` : ""}${project} › ${workspace} · ${workspaceTypeLabel}`}
    >
      <span className="grid h-11 w-[37px] shrink-0 place-items-center">
        <span className="grid h-5 w-5 place-items-center rounded bg-rail-foreground text-[10.5px] font-bold text-rail shadow-sm">
          {normalizedWorkspaceType ? (
            <WorkspaceTypeIcon className="h-3.5 w-3.5" strokeWidth={1.75} />
          ) : (
            projectInit || <FolderKanban className="h-3.5 w-3.5" strokeWidth={2} />
          )}
        </span>
      </span>
      {expanded && (
        <>
          <span className="grid min-w-0 flex-1 text-left leading-tight py-1">
            <span className="truncate text-[12px] font-semibold text-rail-foreground">
              {project}
            </span>
            <span className="truncate text-[10px] text-rail-muted">
              {workspace} · {workspaceTypeLabel}
            </span>
          </span>
          <ChevronsUpDown
            className="mr-2 h-3.5 w-3.5 shrink-0 text-rail-muted"
            strokeWidth={1.75}
          />
        </>
      )}
    </button>
  );

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      {expanded ? (
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={12} className={tooltipDark}>
            {orgName ? `${orgName} · ` : ""}
            {project} › {workspace} · {workspaceTypeLabel}
          </TooltipContent>
        </Tooltip>
      )}

      <PopoverContent
        //   onOpenAutoFocus={() => console.log("Popover mounted")}
        onCloseAutoFocus={() => console.log("Popover closed")}
        side="right"
        align="start"
        sideOffset={10}
        collisionPadding={12}
        closeOnOutsideScroll={false}
        onOpenAutoFocus={(e) => {
          console.log("Popover mounted");
          e.preventDefault();
        }}
        className="z-[1200] w-[460px] overflow-hidden rounded-xl border border-rail-border bg-rail p-0 text-rail-foreground shadow-[0_20px_50px_rgba(0,0,0,0.55)]"
      >
        {/* Organization header — makes the current org unmistakable. */}
        <div className="flex items-center gap-2.5 border-b border-rail-border px-3 py-2">
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-rail-foreground/15 text-[10px] font-bold text-rail-foreground">
            {orgName ? initialsOf(orgName) : <Layers className="h-3.5 w-3.5" />}
          </span>
          <div className="min-w-0 flex-1 leading-tight">
            <p className="text-[9.5px] font-semibold uppercase tracking-[0.09em] text-rail-muted">
              Organization
            </p>
            <p className="truncate text-[12.5px] font-semibold text-rail-foreground">
              {orgName || "—"}
            </p>
          </div>
        </div>

        <div className="flex h-[268px]">
          {/* Left: projects */}
          <div className="flex w-[210px] shrink-0 flex-col border-r border-rail-border">
            <div className="flex items-center gap-2 border-b border-rail-border px-3 py-2">
              <Search className="h-3.5 w-3.5 shrink-0 text-rail-muted" />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Find project..."
                className="flex-1 bg-transparent text-[12.5px] text-rail-foreground outline-none placeholder:text-rail-muted"
              />
            </div>
            <div className="flex-1 overflow-y-auto py-1 no-scrollbar">
              <p className="px-3 pb-1 pt-1.5 text-[9.5px] font-semibold uppercase tracking-[0.09em] text-rail-muted">
                Projects
              </p>
              {isProjectsLoading ? (
                <div className="flex flex-col gap-1 px-3 py-1">
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="h-8 w-full animate-pulse rounded-md bg-rail-foreground/[0.06]"
                    />
                  ))}
                </div>
              ) : filteredProjects.length === 0 ? (
                <p className="px-3 py-4 text-[12px] text-rail-muted">No projects found.</p>
              ) : (
                filteredProjects.map((p: any) => {
                  const isActive = Number(p.id) === activeProjectId;
                  const isHighlighted = Number(p.id) === displayProjectId;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setSelectedProjectId(Number(p.id))}
                      className={cn(
                        "flex w-full items-center gap-2.5 px-3 py-1.5 text-left transition-colors",
                        isHighlighted ? "bg-rail-foreground/10" : "hover:bg-rail-foreground/[0.06]",
                      )}
                    >
                      <FolderKanban
                        className="h-3.5 w-3.5 shrink-0 text-rail-muted"
                        strokeWidth={1.75}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[12.5px] font-medium text-rail-foreground">
                          {p.name}
                        </p>
                        <p className="truncate text-[10.5px] text-rail-muted">
                          {p.identifier ?? `PRO-${p.id}`}
                          {p.workspaceCount != null
                            ? ` · ${p.workspaceCount} workspace${p.workspaceCount !== 1 ? "s" : ""}`
                            : ""}
                        </p>
                      </div>
                      {isActive && <Check className="h-3.5 w-3.5 shrink-0 text-rail-foreground" />}
                    </button>
                  );
                })
              )}
            </div>
            <div className="border-t border-rail-border p-1">
              <button
                onClick={() => {
                  changeOpen(false);
                  navigate("/settings/projects");
                }}
                className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-[12px] text-rail-muted transition-colors hover:bg-rail-foreground/[0.08] hover:text-rail-foreground"
              >
                <Plus className="h-3.5 w-3.5" />
                New project
              </button>
            </div>
          </div>

          {/* Right: workspaces */}
          <div className="min-w-0 flex-1">
            {displayProjectId ? (
              <RailWorkspacePanel
                projectId={displayProjectId}
                projectName={displayProject?.name ?? project}
                activeWorkspaceId={activeWorkspaceId}
                isApplying={isApplying}
                onSelect={handleSelectWorkspace}
                onAddWorkspace={() => {
                  changeOpen(false);
                  navigate(`/settings/projects?projectId=${displayProjectId}&tab=workspaces`);
                }}
              />
            ) : (
              <div className="flex h-full items-center justify-center px-4 text-center text-[12px] text-rail-muted">
                Select a project to see workspaces
              </div>
            )}
          </div>
        </div>

        {isApplying && (
          <div className="flex items-center gap-2 border-t border-rail-border px-3 py-2 text-[11px] text-rail-muted">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Switching scope…
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default ScopeSwitcherRail;
